import { useLoaderData, useLocation, useNavigate } from 'react-router';
import { paths } from '../../../../../common/constants/pathVariables.js';
import { DeviceSelectionProductTypeCoCo } from '../../../../../common/enums.js';
import { useHideGlobalFooter } from '../../../../../common/hooks/useHideGlobalFooter.js';
import {
  deviceListsMsg,
  deviceSelectionMsg,
  newDeviceSelectionMsg,
  omaElisaForCompaniesMsg,
  summaryMsg,
  t,
} from '../../../../../common/i18n/index.js';
import {
  deviceSelectionTabPath,
  getEditDeviceSelectionPath,
} from '../../../../../common/utils/deviceListTabsUtilsCoCo.js';
import { toDeviceSelectionVariantDevices } from '../../../../../common/utils/deviceSelectionDeviceUtilsCoCo.js';
import { getDeviceSelectionEditDevices } from '../../../../../common/utils/deviceSelectionEditUtilsCoCo.js';
import { toDeviceSelectionProductType } from '../../../../../common/utils/deviceSelectionListUtilsCoCo.js';
import { BreadCrumbs } from '../../../../../components/BreadCrumbs/index.js';
import { DetailsWrapper } from '../../../../../components/DetailsWrapper/index.js';
import { DeviceSelectionFormCoCo } from '../../../../../components/DeviceSelectionFormCoCo/DeviceSelectionFormCoCo.js';
import { DeviceSelectionSummaryCoCo } from '../../../../../components/DeviceSelectionFormCoCo/components/DeviceSelectionSummaryCoCo.js';
import type {
  DeviceSelectionCatalogLoaderDataCoCo,
  DeviceSelectionDetailsLoaderDataCoCo,
} from '../../../../../common/loaders.js';
import type { DeviceVariantGroupsByTypeCoCo } from '../../../../../common/utils/deviceSelectionDeviceUtilsCoCo.js';
import type { DeviceSelectionSummaryStateCoCo } from '../../../../../common/utils/deviceSelectionFormUtilsCoCo.js';

const SUMMARY_QUERY_PARAM = 'summary';

const getSearchWithSummary = (search: string) => {
  const params = new URLSearchParams(search);
  params.set(SUMMARY_QUERY_PARAM, '1');
  const query = params.toString();
  return query ? `?${query}` : '';
};

const getSearchWithoutSummary = (search: string) => {
  const params = new URLSearchParams(search);
  params.delete(SUMMARY_QUERY_PARAM);
  const query = params.toString();
  return query ? `?${query}` : '';
};

const getInitialValues = (
  selection: DeviceSelectionSummaryStateCoCo | null,
  deviceSelection?: DeviceSelectionDetailsLoaderDataCoCo['deviceSelection']
) => {
  if (selection) {
    return selection;
  }
  if (!deviceSelection) {
    return undefined;
  }
  return {
    name: deviceSelection.name,
    description: deviceSelection.description ?? '',
    productType: toDeviceSelectionProductType(deviceSelection.productType),
    selectedDeviceIds: deviceSelection.items.map(item => item.variantGroupId),
  };
};

export const CompanyInfoDeviceSelectionPageCoCo = ({
  deviceSelection,
  deviceVariantGroups,
}: {
  deviceSelection?: DeviceSelectionDetailsLoaderDataCoCo['deviceSelection'];
  deviceVariantGroups: DeviceVariantGroupsByTypeCoCo;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (deviceSelection && !params.has('mdmId')) {
    params.set('mdmId', deviceSelection.accountMdmId);
  }
  const search = params.size > 0 ? `?${params}` : '';
  const selection = location.state as DeviceSelectionSummaryStateCoCo | null;
  const isSummary = Boolean(selection) && new URLSearchParams(search).get(SUMMARY_QUERY_PARAM) === '1';
  const editProductType = deviceSelection && toDeviceSelectionProductType(deviceSelection.productType);
  const initialValues = getInitialValues(selection, deviceSelection);
  const devicesByProductType = {
    [DeviceSelectionProductTypeCoCo.EPP]: toDeviceSelectionVariantDevices(
      deviceVariantGroups,
      DeviceSelectionProductTypeCoCo.EPP
    ),
    [DeviceSelectionProductTypeCoCo.PURCHASED]: toDeviceSelectionVariantDevices(
      deviceVariantGroups,
      DeviceSelectionProductTypeCoCo.PURCHASED
    ),
  };
  if (deviceSelection && editProductType) {
    devicesByProductType[editProductType] = getDeviceSelectionEditDevices(
      devicesByProductType[editProductType],
      deviceSelection
    );
  }
  const devices = devicesByProductType[selection?.productType ?? editProductType ?? DeviceSelectionProductTypeCoCo.EPP];
  const unresolvedItems = deviceSelection?.items.filter(
    item => !devices.some(device => device.id === item.variantGroupId)
  );
  const formPath = deviceSelection
    ? getEditDeviceSelectionPath(deviceSelection.uuid)
    : paths.COMPANY_INFO_DEVICE_SELECTION_NEW;
  const heading = deviceSelection ? t.P81H('Edit device selection') : t.DLV8(newDeviceSelectionMsg);
  // The form has its own sticky footer
  useHideGlobalFooter({ keepHeader: true });

  return (
    <DetailsWrapper
      classes={['of-device-selection-new-coco']}
      detailsTop={
        <BreadCrumbs
          breadCrumbPaths={[
            { name: t.VCUZ(omaElisaForCompaniesMsg), path: paths.SELF_SERVICE_HOME },
            { name: t.COBB(deviceListsMsg), path: paths.COMPANY_INFO_CATALOGS },
            { name: t.AHB4(deviceSelectionMsg), path: deviceSelectionTabPath },
            { name: heading },
          ]}
        />
      }
      id={isSummary ? 'device-selection-summary' : 'device-selection-new'}
      heading={heading}
      headingTop={isSummary ? t.VDB4(summaryMsg) : undefined}
      // TODO: replace with the final device selection icon once it has been decided
      heroPicto="catalog"
    >
      {isSummary && selection ? (
        <DeviceSelectionSummaryCoCo
          selection={selection}
          originalSelection={deviceSelection}
          devices={devices}
          onReturn={() =>
            navigate(formPath + getSearchWithoutSummary(search), {
              state: selection,
            })
          }
        />
      ) : (
        <DeviceSelectionFormCoCo
          initialValues={initialValues}
          availableProductTypes={editProductType ? [editProductType] : undefined}
          lockedProductType={editProductType}
          devicesByProductType={devicesByProductType}
          unresolvedItems={unresolvedItems}
          onValidSubmit={values => navigate(formPath + getSearchWithSummary(search), { state: values })}
        />
      )}
    </DetailsWrapper>
  );
};

export const CompanyInfoDeviceSelectionEditPageCoCo = () => {
  const { deviceSelection, deviceVariantGroups } = useLoaderData() as DeviceSelectionDetailsLoaderDataCoCo;
  return (
    <CompanyInfoDeviceSelectionPageCoCo
      key={`${deviceSelection.uuid}-${deviceSelection.accountMdmId}`}
      deviceSelection={deviceSelection}
      deviceVariantGroups={deviceVariantGroups}
    />
  );
};

export const CompanyInfoDeviceSelectionNewPageCoCo = () => {
  const data = useLoaderData() as DeviceSelectionCatalogLoaderDataCoCo;
  return <CompanyInfoDeviceSelectionPageCoCo deviceVariantGroups={data.deviceVariantGroups} />;
};
